/**
 * Marketing site redirect helpers. Production sends app root traffic to findyourkind.com;
 * local dev and OAuth callbacks must stay on the current origin.
 */

/** Public marketing site (matches RedirectToLandingPage / LandingPage external redirect). */
export const MARKETING_SITE_ORIGIN = 'https://findyourkind.com/';

/** True when the URL looks like an OAuth/OIDC redirect (e.g. Auth0 PKCE on ?code=&state=). */
export const isOAuthOrOpenIdCallbackSearch = search => {
  if (!search || typeof search !== 'string') {
    return false;
  }
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(normalized);
  return params.has('code') || params.has('state') || params.has('error');
};

/**
 * True when the hash fragment looks like OAuth/OIDC (implicit flow uses #access_token=...).
 * @param {string} [hash]
 */
export const isOAuthOrOpenIdCallbackHash = hash => {
  if (!hash || typeof hash !== 'string') {
    return false;
  }
  const h = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!h) {
    return false;
  }
  const params = new URLSearchParams(h);
  return (
    params.has('code') ||
    params.has('state') ||
    params.has('error') ||
    params.has('access_token') ||
    params.has('id_token')
  );
};

/** True when query or hash indicates an OAuth/OIDC callback (Auth0 PKCE or implicit). */
export const isOAuthOrOpenIdCallbackUrl = (search, hash) =>
  isOAuthOrOpenIdCallbackSearch(search) || isOAuthOrOpenIdCallbackHash(hash);

/**
 * True when the app root should not fetch hosted landing-page CMS assets (faster path).
 * OAuth callbacks, production redirect-to-marketing, and SSR for / without ?redirect=false skip fetch.
 * @param {string} [search]
 * @param {string} [hash] - client-only; pass empty on SSR
 */
export const shouldSkipLandingPageHostedAssets = (search, hash = '') => {
  const s = typeof search === 'string' ? search : '';
  const h = typeof hash === 'string' ? hash : '';
  if (isOAuthOrOpenIdCallbackUrl(s, h)) {
    return true;
  }
  if (typeof window !== 'undefined' && isLocalDevelopmentHost()) {
    return false;
  }
  const params = new URLSearchParams(s.startsWith('?') ? s.slice(1) : s);
  if (params.get('redirect') === 'false') {
    return false;
  }
  return true;
};

/**
 * True when the client should send the user to the marketing site (browser only; useLayoutEffect).
 * Not used on local dev; stays on app for OAuth/OIDC callbacks and ?redirect=false.
 * @param {string} [search]
 * @param {string} [hash] - include location.hash so implicit / hash-based callbacks do not redirect away
 */
export const shouldRedirectRootToMarketingSite = (search, hash = '') => {
  const s = typeof search === 'string' ? search : '';
  const h = typeof hash === 'string' ? hash : '';
  const params = new URLSearchParams(s.startsWith('?') ? s.slice(1) : s);
  if (params.get('redirect') === 'false') {
    return false;
  }
  // Local debug: ?debugRootRedirect=1 simulates production root redirect behaviour
  if (params.get('debugRootRedirect') === '1') {
    return true;
  }
  if (typeof window !== 'undefined' && isLocalDevelopmentHost()) {
    return false;
  }
  if (isOAuthOrOpenIdCallbackUrl(s, h)) {
    return false;
  }
  return true;
};

/** Host suffixes used by HTTPS tunnels for local Stripe wallet proof. */
export const LOCAL_DEV_TUNNEL_SUFFIXES = [
  'ngrok.io',
  'ngrok-free.app',
  'ngrok.app',
  'trycloudflare.com',
  'loca.lt',
];

const hostnameFromEnvValue = raw => {
  if (!raw || typeof raw !== 'string') {
    return '';
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  try {
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    return new URL(withProtocol).hostname.toLowerCase();
  } catch (_err) {
    return trimmed.split('/')[0].split(':')[0].toLowerCase();
  }
};

/**
 * True for localhost and for HTTPS tunnel hosts used in local wallet proof.
 * Live `app.findyourkind.com` must stay false so the marketing redirect still runs.
 */
export const isLocalDevelopmentHostname = hostname => {
  if (!hostname || typeof hostname !== 'string') {
    return false;
  }
  const host = hostname.toLowerCase().split(':')[0];
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return true;
  }
  const configuredHost = hostnameFromEnvValue(process.env.REACT_APP_DEV_TUNNEL_HOST);
  if (configuredHost && host === configuredHost) {
    return true;
  }
  return LOCAL_DEV_TUNNEL_SUFFIXES.some(suffix => host === suffix || host.endsWith(`.${suffix}`));
};

export const isLocalDevelopmentHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return isLocalDevelopmentHostname(window.location.hostname);
};
